<script setup lang="ts">
import VueApexCharts from 'vue3-apexcharts'
import { computed } from 'vue'

interface IDonutChartSegment {
  label: string
  value: number
  color?: string
}

const props = withDefaults(
  defineProps<{
    title: string
    segments: IDonutChartSegment[]
    height?: number
  }>(),
  {
    height: 320,
  },
)

const DEFAULT_COLORS = ['#123ADD', '#80CAEE', '#04304c', '#75787B']

const series = computed(() => props.segments.map((s) => s.value))

const labels = computed(() => props.segments.map((s) => s.label))

const colors = computed(() =>
  props.segments.map((s, i) => s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]),
)

const chartOptions = computed(() => {
  const legendMarkers = {
    shape: 'square' as const,
    width: 10,
    height: 10,
    radius: 0,
  }

  return {
    labels: labels.value,
    colors: colors.value,
    chart: {
      fontFamily: 'Satoshi, sans-serif',
      toolbar: { show: false },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
    },
    legend: {
      show: true,
      position: 'right' as const,
      fontSize: '14px',
      fontWeight: 600,
      labels: {
        colors: '#000A1E',
      },
      markers: legendMarkers,
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: { show: true },
            value: { show: true, fontSize: '16px', fontWeight: 'bold' },
            total: {
              show: false,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#FFFFFF'],
      },
    },
    stroke: {
      width: 0,
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          legend: {
            position: 'bottom' as const,
            markers: legendMarkers,
          },
        },
      },
    ],
  }
})

const chartKey = computed(() => `${labels.value.join('|')}-${series.value.join(',')}`)
</script>

<template>
  <div class="flex h-full min-h-0 w-full min-w-0 max-w-full flex-col gap-8">
    <h2 class="shrink-0 font-satoshi text-title-sm font-bold leading-snug text-black">
      {{ title }}
    </h2>
    <div
      class="flex min-h-[320px] min-w-0 max-w-full flex-1 flex-col items-center justify-center overflow-hidden bg-white p-4 shadow-md md:flex-row md:items-center md:justify-center">
      <VueApexCharts
        :key="chartKey"
        type="donut"
        :height="height"
        class="w-full max-w-md"
        :options="chartOptions"
        :series="series" />
    </div>
  </div>
</template>
